import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
// Note: @mui/icons-material not installed, using text instead
import TokenExclusionFilter from './TokenExclusionFilter';
import ExchangeTogglesV2 from './ExchangeTogglesV2';
import FeeEditor from './FeeEditor';
import ThresholdControls from './ThresholdControls';
import SortOptions from './SortOptions';
import { NetworkStatusPanel } from './NetworkStatusPanel';
import BudgetControl from './BudgetControl';
import type { Settings } from '../../types/settings';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  currentSettings: Settings;
  onApply: (settings: Settings) => void;
  onReset: () => void;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  currentSettings,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const [tempSettings, setTempSettings] = useState<Settings>(currentSettings);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const prevOpenRef = React.useRef(open);

  // Only update temp settings when dialog transitions from closed to open
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setTempSettings(currentSettings);
    }
    prevOpenRef.current = open;
  }, [open, currentSettings]);

  const handleApply = () => {
    onApply(tempSettings);
    onClose();
  };

  const handleCancel = () => {
    setTempSettings(currentSettings); // Reset to original
    onClose();
  };

  const handleReset = () => {
    setResetDialogOpen(true);
  };

  const confirmReset = () => {
    onReset();
    setResetDialogOpen(false);
    onClose();
  };

  const updateFilters = (filters: Partial<Settings['filters']>) => {
    setTempSettings(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filters }
    }));
  };

  const updateBuyExchanges = (buyExchanges: Settings['buyExchanges']) => {
    setTempSettings(prev => ({ ...prev, buyExchanges }));
  };

  const updateSellExchanges = (sellExchanges: Settings['sellExchanges']) => {
    setTempSettings(prev => ({ ...prev, sellExchanges }));
  };

  const updateFeeOverrides = (feeOverrides: Settings['feeOverrides']) => {
    setTempSettings(prev => ({ ...prev, feeOverrides }));
  };

  const updateDisplay = (display: Partial<Settings['display']>) => {
    setTempSettings(prev => ({
      ...prev,
      display: { ...prev.display, ...display }
    }));
  };

  const updateFeatures = (features: Partial<Settings['features']>) => {
    setTempSettings(prev => ({
      ...prev,
      features: { ...prev.features, ...features }
    }));
  };

  const updateStrategyBudget = (budget: number) => {
    setTempSettings(prev => ({
      ...prev,
      strategyBudget: budget
    }));
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('settings.title')}</Typography>
            <IconButton onClick={handleCancel} size="small">
              ✕
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Features Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.features')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.features?.enableFutures ?? true}
                  onChange={(e) => updateFeatures({ enableFutures: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('settings.features.enableFutures')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('settings.features.enableFuturesDescription')}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Filters Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.filters')}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TokenExclusionFilter
              excludedTokens={tempSettings.filters.excludedTokens}
              onChange={(tokens) => updateFilters({ excludedTokens: tokens })}
            />

            <ThresholdControls
              minProfitPercent={tempSettings.filters.minProfitPercent}
              maxProfitPercent={tempSettings.filters.maxProfitPercent}
              minVolumeUSD={tempSettings.filters.minVolumeUSD}
              tradeAmountUSD={tempSettings.filters.tradeAmountUSD}
              networkStatusFilter={tempSettings.filters.networkStatus}
              onProfitChange={(value) => updateFilters({ minProfitPercent: value })}
              onMaxProfitChange={(value) => updateFilters({ maxProfitPercent: value })}
              onVolumeChange={(value) => updateFilters({ minVolumeUSD: value })}
              onTradeAmountChange={(value) => updateFilters({ tradeAmountUSD: value })}
              onNetworkStatusFilterChange={(value) => updateFilters({ networkStatus: value })}
            />
          </Box>

          {/* Strategy Execution Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.strategyExecution')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <BudgetControl
              value={tempSettings.strategyBudget}
              onChange={updateStrategyBudget}
              minBudget={6}
              maxBudget={1000000}
            />
          </Box>

          {/* Exchanges Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.exchanges')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ExchangeTogglesV2
              buyExchanges={tempSettings.buyExchanges || {}}
              sellExchanges={tempSettings.sellExchanges || {}}
              onBuyChange={updateBuyExchanges}
              onSellChange={updateSellExchanges}
            />
          </Box>

          {/* Fee Customization Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.feeCustomization')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FeeEditor
              feeOverrides={tempSettings.feeOverrides}
              onChange={updateFeeOverrides}
            />
          </Box>

          {/* Display Options Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.displayOptions')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <SortOptions
              sortBy={tempSettings.display.sortBy}
              onChange={(sortBy) => updateDisplay({ sortBy })}
            />
          </Box>

          {/* Network Status Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('settings.sections.networkStatus')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <NetworkStatusPanel />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleReset} color="error">
            {t('settings.reset')}
          </Button>
          <Box flexGrow={1} />
          <Button onClick={handleCancel}>{t('settings.cancel')}</Button>
          <Button onClick={handleApply} variant="contained" color="primary">
            {t('settings.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>{t('settings.resetConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('settings.resetConfirm.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>{t('settings.resetConfirm.cancel')}</Button>
          <Button onClick={confirmReset} color="error" variant="contained">
            {t('settings.resetConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(SettingsDialog);
